<div class="h-dvh lg:h-screen flex flex-col overflow-hidden bg-gray-50 lg:p-4">
  <!-- Navbar at top - Floating Card on large screens, flat bar on compact -->
  <div class="lg:mb-4">
    <lib-navbar
      [isDataLocked]="encryptionService.getEncryptionState().isLocked"
      [showMenuButton]="layoutService.isCompact()"
      [isMenuOpen]="layoutService.isDrawerOpen()"
      [menuControlsId]="drawerId"
      (menuClick)="openDrawer()"
      (lockClick)="handleLockClick()"
      (bellClick)="handleBellClick()"></lib-navbar>
  </div>

  <div class="flex flex-1 min-h-0 lg:gap-4 overflow-hidden">
    <!-- Sidebar on left - Floating Card - Full height (large screens) -->
    @if (!layoutService.isCompact()) {
    <lib-sidebar
      [menuItems]="menuItems()"
      (menuClick)="handleMenuClick($event)"
      (signOut)="handleSignOut()"></lib-sidebar>
    }

    <!-- Main content area with router-outlet and confirmation section -->
    <div class="flex-1 min-w-0 flex flex-col lg:gap-4 overflow-hidden">
      <!-- Main content area - Floating Card on large screens, full bleed on compact -->
      <main
        class="flex-1 overflow-y-auto bg-surface-bg-generic-white p-4 lg:p-6 lg:border lg:border-border-neutral-subtle lg:rounded-xl">
        <router-outlet></router-outlet>
      </main>

      @if (confirmationService.showConfirmation()) {
      <!-- Floating Confirmation Dialog - Only visible when needed -->
      <app-floating-confirmation />
      }
    </div>
  </div>
</div>

<!-- Navigation Drawer (compact screens) -->
@if (layoutService.isCompact()) {
<div
  class="fixed inset-0 z-[990] bg-surface-bg-generic-black transition-opacity duration-300 motion-reduce:transition-none"
  [class]="
    layoutService.isDrawerOpen() ? 'opacity-20' : 'opacity-0 pointer-events-none'
  "
  aria-hidden="true"
  (click)="closeDrawer()"></div>

<div
  [id]="drawerId"
  role="dialog"
  aria-modal="true"
  aria-label="Main navigation"
  class="fixed inset-y-0 start-0 z-[991] w-[85vw] max-w-80 flex flex-col bg-surface-bg-generic-white shadow-xl overscroll-contain transition-transform duration-300 ease-out motion-reduce:transition-none"
  [class]="
    layoutService.isDrawerOpen()
      ? 'translate-x-0'
      : '-translate-x-full rtl:translate-x-full'
  "
  [attr.inert]="layoutService.isDrawerOpen() ? null : ''">
  <div
    class="flex items-center justify-between px-6 py-3 border-b border-border-neutral-soft">
    <lib-arm-logo width="56" class="flex-shrink-0" />
    <button
      #drawerCloseButton
      type="button"
      id="drawer_close_btn"
      class="flex items-center justify-center min-w-11 min-h-11 -me-2 rounded-lg text-typography-neutral-solid hover:bg-surface-bg-neutral-subtle transition-colors cursor-pointer"
      aria-label="Close navigation menu"
      (click)="closeDrawer()">
      <lib-close-icon [width]="'20px'" [height]="'20px'" />
    </button>
  </div>

  <div class="flex-1 min-h-0">
    <lib-sidebar
      [mode]="sidebarMode.Drawer"
      [menuItems]="menuItems()"
      (menuClick)="handleMenuClick($event)"
      (signOut)="handleSignOut()"></lib-sidebar>
  </div>
</div>
}

<!-- Global Modal Component -->
<lib-modal></lib-modal>

@if (mainService.offCanvasConfig().show) {
<div
  class="offCanvas-wrapper"
  [style.--offcanvas-width]="mainService.offCanvasConfig().width || '27.75rem'">
  <div
    class="offCanvas-container bg-white p-4 sm:p-measure-2xl sm:m-4 sm:rounded-md lg:m-8">
    <div class="offCanvas-content h-full flex flex-col">
      <!-- Pre-Header Template (Optional) - Renders above the header -->
      @if (mainService.offCanvasConfig().preHeaderTemplate) {
      <div class="mb-6">
        <ng-container
          [ngTemplateOutlet]="mainService.offCanvasConfig().preHeaderTemplate"
          [ngTemplateOutletContext]="{
              $implicit: mainService.offCanvasConfig().data,
            }" />
      </div>
      } @if (mainService.offCanvasConfig().title ||
      mainService.offCanvasConfig().topActions ||
      mainService.offCanvasConfig().hasCloseButton) {
      <div
        class="flex items-center justify-between gap-4 border-b border-gray-200 pb-4">
        <p class="text-gray-900 text-lg font-semibold wrap-break-word">
          {{ mainService.offCanvasConfig().title }}
        </p>

        <div class="flex items-center gap-2">
          @if (mainService.offCanvasConfig().topActions) {
          <ng-container
            [ngTemplateOutlet]="mainService.offCanvasConfig().topActions"
            [ngTemplateOutletContext]="{
                $implicit: mainService.offCanvasConfig().data,
              }" />
          } @if (mainService.offCanvasConfig().hasCloseButton) {
          <button
            [disabled]="mainService.offCanvasConfig().notAllowedToClose"
            class="rounded-sm transition-colors max-lg:inline-flex max-lg:items-center max-lg:justify-center max-lg:min-w-11 max-lg:min-h-11 max-lg:-me-3"
            type="button"
            aria-label="Close panel"
            (click)="closeOffCanvas()">
            <lib-close-icon [width]="'20px'" [height]="'20px'" />
          </button>
          }
        </div>
      </div>
      }
      <div class="flex-1 min-h-0">
        @if (mainService.offCanvasConfig().showLoader) {
        <div class="offcanvas-loader flex justify-center items-center">
          <app-loader />
        </div>
        } @else {
        <ng-container
          [ngTemplateOutlet]="mainService.offCanvasConfig().template"
          [ngTemplateOutletContext]="{
              $implicit: mainService.offCanvasConfig().data,
            }" />
        }
      </div>
    </div>
  </div>
</div>
<div class="offCanvas-overlay bg-surface-bg-generic-black"></div>
}

<!-- Unlock Form Template -->
<ng-template #unlockFormTemplate>
  <app-unlock-form
    (dataUnlocked)="handleUnlockSuccess()"
    (cancelled)="handleOffcanvasClose()"></app-unlock-form>
</ng-template>

<!-- Lock Form Template -->
<ng-template #lockFormTemplate>
  <app-lock-form
    (locked)="handleLockSuccess()"
    (generateKey)="handleGenerateNewKey()"
    (cancelled)="handleOffcanvasClose()"></app-lock-form>
</ng-template>

<!-- Generate Key Form Template -->
<ng-template #generateKeyFormTemplate>
  <app-generate-key-form
    (keyGenerated)="handleGenerateKeySuccess()"
    (cancelled)="handleOffcanvasClose()"></app-generate-key-form>
</ng-template>
